import type { AnyAction, Middleware, ThunkDispatch } from '@reduxjs/toolkit';
import type { RootState } from '../../store/store';
import { baseApi } from '../../store/services/BaseService';
import { profilesApi } from '../../store/services/ProfilesService';
import { authApi } from '../../store/services/AuthService';
import { clearAdminData, refreshAccess } from './access';

type AccessDispatch = ThunkDispatch<RootState, unknown, AnyAction>;
export const accessMiddleware: Middleware<unknown, RootState, AccessDispatch> = api => {
  let rolesDirty = false;
  let refreshing: { generation: number; requestId: string; promise: Promise<unknown> } | undefined;
  const generations = new Map<string, number>();
  return next => action => {
    const before = api.getState().auth;
    const endpoint = action.meta?.arg?.endpointName;
    const requestId = action.meta?.requestId;
    const adminRequest = action.type.startsWith(baseApi.reducerPath + '/') && endpoint;
    if (adminRequest && action.type.endsWith('/pending')) generations.set(requestId, before.generation);
    if (adminRequest && /\/(fulfilled|rejected)$/.test(action.type)) {
      // A queued write may be reauthorized in a newer generation after another
      // action was revoked. Its transport records that generation explicitly.
      const generation = action.meta?.baseQueryMeta?.accessGeneration ?? generations.get(requestId);
      generations.delete(requestId);
      if (generation !== undefined && generation !== before.generation) return action;
    }
    if (refreshAccess.match(action)) {
      const checking = before.accessStatus === 'loading' || before.accessStatus === 'refreshing';
      if (checking && refreshing?.generation === before.generation && refreshing.requestId === before.accessRequestId) {
        return refreshing.promise;
      }
      const running = api.dispatch(profilesApi.util.getRunningQueryThunk('getCurrentUserProfile', undefined));
      // RTK's running-query registry can still contain an aborted old session
      // until its promise settles. Never reuse it after login/reset.
      const request = running && running.requestId === before.accessRequestId && checking ? running :
        api.dispatch(profilesApi.endpoints.getCurrentUserProfile.initiate(
          undefined, { forceRefetch: true, subscribe: true },
        ));
      const current = {
        generation: before.generation,
        requestId: request.requestId,
        promise: Promise.resolve(request).finally(() => {
          request.unsubscribe?.();
          if (refreshing === current) refreshing = undefined;
        }),
      };
      refreshing = current;
      return current.promise;
    }
    const result = next(action);
    const after = api.getState().auth;
    const lost = before.availableActions.some(a => !after.availableActions.some(b => b.name === a.name)) ||
      (before.platformRoles.includes('ADMIN') && !after.platformRoles.includes('ADMIN'));
    const loggedOut = action.type === 'auth/loggedOut' || authApi.endpoints.logout.matchFulfilled(action);
    const loggedIn = authApi.endpoints.login.matchFulfilled(action);
    if (loggedOut || loggedIn || (profilesApi.endpoints.getCurrentUserProfile.matchFulfilled(action) &&
      after.accessStatus === 'ready' && (lost || before.profile?.userId !== after.profile?.userId))) {
      api.dispatch(clearAdminData());
      const running = api.dispatch(baseApi.util.getRunningQueriesThunk());
      running.forEach(request => { if (request.requestId !== requestId) request.abort(); });
      api.dispatch(baseApi.util.resetApiState());
    }
    if (profilesApi.endpoints.getCurrentUserProfile.matchFulfilled(action) && after.accessStatus === 'ready' && rolesDirty) {
      rolesDirty = false;
      api.dispatch(baseApi.util.invalidateTags(['PlatformRoles']));
    }
    if (loggedOut) rolesDirty = false;
    if (loggedIn) api.dispatch(refreshAccess());
    if ((endpoint === 'grantPlatformRole' || endpoint === 'revokePlatformRole') && action.type.endsWith('/fulfilled')) {
      rolesDirty = true;
      api.dispatch(refreshAccess());
    }
    return result;
  };
};
